import React from 'react'

import { Box, Skeleton, Stack } from '@mui/material'

import { LIST_ROW_LEADING_SIZE } from 'components/UI'

/** Rows drawn while the first page loads. Roughly one phone screen's worth. */
const PLACEHOLDER_ROWS = 7

export interface ActivityFeedSkeletonProps {
  /**
   * How many placeholder rows to draw.
   *
   * The default is a screenful, for the FIRST load where the skeleton stands in
   * for the whole page. Appending a further page wants a short run instead —
   * see the note on the component about why appending gets a skeleton at all.
   */
  rows?: number
}

/**
 * Loading state for the cross-certificate Activity feed (VERA-315).
 *
 * Footprint parity, not decoration (working-conventions §19): every dimension
 * below is taken from the real row rather than picked to look plausible — the
 * 40px `LIST_ROW_LEADING_SIZE` slot, the same `py: 2` (16px on this app's 8px
 * MUI base), the same bottom hairline, the same two-line label/sublabel column
 * and the same right-aligned timestamp. So the swap to real content moves
 * nothing.
 *
 * The page header is NOT skeletoned: its title and description are static
 * strings that render on the first paint, so a placeholder there would replace
 * content that is already correct with a grey bar.
 *
 * ## It is also the APPEND indicator, in place of a spinner
 *
 * Loading the next page used to show a centred CircularProgress. A spinner is
 * the one thing §19 exists to replace: it says "something is happening" and
 * nothing about what, while these rows say "more of exactly this is coming" and
 * reserve the space it will occupy. It is the same argument as the first load,
 * so it gets the same component rather than a second, different treatment.
 *
 * A short run there, not a screenful — the rows sit below content the reader is
 * already looking at, so their job is to show that the list continues, not to
 * stand in for a page.
 */
export const ActivityFeedSkeleton: React.FC<ActivityFeedSkeletonProps> = ({
  rows = PLACEHOLDER_ROWS,
}) => (
  <Stack spacing={0}>
    {Array.from({ length: rows }).map((_, index) => (
      <Stack
        key={index}
        direction='row'
        alignItems='center'
        spacing={2}
        sx={theme => ({
          py: 2,
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          '&:last-of-type': { borderBottom: 'none' },
        })}
      >
        <Box
          sx={{
            width: LIST_ROW_LEADING_SIZE,
            height: LIST_ROW_LEADING_SIZE,
            minWidth: LIST_ROW_LEADING_SIZE,
            flexShrink: 0,
          }}
        >
          {/* Rounded rect, not a circle: the real leading slot holds a product
              photo with the same 8px radius, and a disc here would promise an
              avatar the row never shows. */}
          <Skeleton
            variant='rectangular'
            width={LIST_ROW_LEADING_SIZE}
            height={LIST_ROW_LEADING_SIZE}
            sx={{ borderRadius: '8px' }}
          />
        </Box>

        <Stack sx={{ flexGrow: 1, minWidth: 0 }} spacing={0.5}>
          {/* Widths alternate so the column reads as a list of differing event
              names rather than a stack of identical bars. */}
          <Skeleton variant='text' width={index % 2 === 0 ? '58%' : '44%'} height={20} />
          <Skeleton variant='text' width={index % 3 === 0 ? '34%' : '48%'} height={18} />
        </Stack>

        <Skeleton variant='text' width={54} height={18} sx={{ flexShrink: 0 }} />
      </Stack>
    ))}
  </Stack>
)

export default ActivityFeedSkeleton
